import React, { useEffect, useState } from "react";
import { IonLoading } from "@ionic/react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, isFirebaseConfigured } from "../firebase";
import { track } from "../services/analytics";
import { completeRedirectSignIn, enabledProviders, sendMagicLink, signInGuest, signInWithProvider, startDiscordSignIn, OAuthProviderId } from "../services/authProviders";
import { ProxyError } from "../services/proxyApi";
import AuthShell, { inputClass, labelClass } from "./AuthShell";
import ProviderButtons from "./ProviderButtons";

export function friendlyAuthError(code: string | undefined, t: (k: string) => string): string {
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/invalid-login-credentials":
      return t("auth.errors.invalidCredentials");
    case "auth/invalid-email":
      return t("auth.errors.invalidEmail");
    case "auth/too-many-requests":
      return t("auth.errors.tooMany");
    case "auth/network-request-failed":
      return t("auth.errors.network");
    case "auth/email-already-in-use":
    case "auth/credential-already-in-use":
      return t("auth.errors.emailInUse");
    case "auth/weak-password":
    case "auth/password-does-not-meet-requirements":
      return t("auth.errors.weakPassword");
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
    case "auth/user-cancelled":
      return t("auth.errors.cancelled");
    case "auth/account-exists-with-different-credential":
      return t("auth.errors.accountExists");
    case "auth/operation-not-allowed":
      return t("auth.errors.providerDisabled");
    case "auth/unauthorized-domain":
      return t("auth.errors.unauthorizedDomain");
    case "auth/requires-recent-login":
      return t("auth.errors.recentLogin");
    case "auth/provider-already-linked":
      return t("auth.errors.alreadyLinked");
    case "auth/invalid-verification-code":
      return t("auth.errors.invalidCode");
    case "auth/invalid-phone-number":
      return t("auth.errors.invalidPhone");
    case "auth/user-disabled":
      return t("auth.errors.disabled");
    default:
      return t("auth.errors.generic");
  }
}

export function errorFrom(e: any, t: (k: string) => string): string {
  if (e instanceof ProxyError) {
    if (e.status === 503 || e.code === "discord_not_configured") return t("auth.errors.providerDisabled");
    if (e.status === 429) return t("auth.errors.tooMany");
    return t("auth.errors.generic");
  }
  return friendlyAuthError(e?.code, t);
}

const Login: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [magic, setMagic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finishingRedirect, setFinishingRedirect] = useState(false);

  // Retour d'une connexion par redirection (navigateurs mobiles) : termine la connexion,
  // sinon le guard de App redirige vers /home dès que l'utilisateur est connu.
  useEffect(() => {
    let cancelled = false;
    setFinishingRedirect(true);
    completeRedirectSignIn()
      .catch((e) => !cancelled && setError(errorFrom(e, t)))
      .finally(() => !cancelled && setFinishingRedirect(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [info, setInfo] = useState<string | null>(null);
  const providers = enabledProviders();

  const run = async (fn: () => Promise<void>) => {
    if (!isFirebaseConfigured) return setError(t("auth.errors.notConfigured"));
    setLoading(true);
    try {
      await fn();
    } catch (e: any) {
      setError(errorFrom(e, t));
    } finally {
      setLoading(false);
    }
  };

  const loginEmail = () => {
    if (!email) return setError(t("auth.errors.invalidEmail"));
    if (magic) {
      return run(async () => {
        await sendMagicLink(email);
        setInfo(t("auth.magic.sent", { email: email.trim() }));
      });
    }
    if (!password) return setError(t("auth.errors.fillAll"));
    return run(async () => {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      track("login_completed", { method: "email" });
    });
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") loginEmail();
  };

  return (
    <AuthShell
      title={t("auth.login.title")}
      subtitle={t("auth.login.subtitle")}
      alert={{
        open: Boolean(error || info),
        header: error ? t("common.error") : t("common.information"),
        message: error || info || "",
        onClose: () => {
          setError(null);
          setInfo(null);
        },
        dismissLabel: t("common.close"),
      }}>
      <IonLoading isOpen={loading || finishingRedirect} message={t("common.loading")} />
      <div className="space-y-4">
        <ProviderButtons
          mode="login"
          disabled={loading}
          onOAuth={(id: OAuthProviderId) => run(() => signInWithProvider(id))}
          onDiscord={() => run(() => startDiscordSignIn("login"))}
          onPhone={providers.includes("phone") ? () => history.push("/phone") : undefined}
        />

        {(providers.includes("password") || providers.includes("emailLink")) && (
          <>
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-2 border-white/15" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-[#121a38] px-4 text-xs text-gray-400 font-bold uppercase tracking-wider">{t("auth.login.orEmail")}</span>
              </div>
            </div>

            <div className="space-y-2">
              <label className={labelClass}>
                <i className="fa-solid fa-envelope text-primary-400" />
                {t("auth.email")}
              </label>
              <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyPress={onKey} placeholder="prenom@exemple.ch" className={inputClass} />
            </div>

            {!magic && providers.includes("password") && (
              <div className="space-y-2">
                <label className={labelClass}>
                  <i className="fa-solid fa-lock text-primary-400" />
                  {t("auth.password")}
                </label>
                <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyPress={onKey} placeholder="••••••••" className={inputClass} />
                <div className="text-right">
                  <button type="button" onClick={() => history.push("/reset-password")} className="text-xs font-medium text-primary-300">
                    {t("auth.login.forgot")}
                  </button>
                </div>
              </div>
            )}

            <button type="button" onClick={loginEmail} className="btn-primary w-full text-base">
              {magic ? t("auth.magic.send") : t("auth.login.button")}
            </button>

            {providers.includes("emailLink") && providers.includes("password") && (
              <button type="button" onClick={() => setMagic(!magic)} className="w-full text-xs text-primary-300 font-semibold py-1">
                <i className={`fa-solid ${magic ? "fa-key" : "fa-wand-magic-sparkles"} mr-1`} />
                {magic ? t("auth.magic.usePassword") : t("auth.magic.useLink")}
              </button>
            )}
            {magic && <p className="text-[11px] text-gray-400 text-center -mt-1">{t("auth.magic.hint")}</p>}

            {providers.includes("password") && (
              <p className="text-xs text-gray-400 text-center">
                {t("auth.login.noAccount")}{" "}
                <button type="button" onClick={() => history.push("/signup")} className="text-success-400 font-semibold">
                  {t("auth.login.signup")}
                </button>
              </p>
            )}
          </>
        )}

        {providers.includes("anonymous") && (
          <button type="button" onClick={() => run(signInGuest)} className="w-full text-sm text-gray-300 py-2 border border-dashed border-white/20 rounded-xl">
            <i className="fa-solid fa-user-secret mr-2 text-gray-400" />
            {t("auth.guest.cta")}
          </button>
        )}
        <p className="text-[11px] text-gray-500 text-center">{t("auth.login.security")}</p>
      </div>
    </AuthShell>
  );
};

export default Login;
